import React from "react";
import { abbreviateNumber } from "js-abbreviation-number";
import { Link } from "react-router-dom";
import slugify from "../utils/slugify";
import LazyImage from "./LazyImage";
import VideoLength from "../shared/VideoLength";
import { MdPlayCircleOutline } from "react-icons/md";

// Clean video card (no uploader avatar/name — author is mostly "Uploaded" noise).
// - Normal: thumbnail on top (16:9) with hover play overlay, then title + "views • time".
// - Compact: small stacked card used in the VideoDetails sidebar grid.
const VideoCard = ({ video, compact = false }) => {
  const slug = slugify(video?.title || video?.videoId || "video");
  const viewsText = `${abbreviateNumber(video?.stats?.views || 0, 2)} views`;
  const publishedText = video?.publishedTimeText || "";
  const meta = publishedText ? `${viewsText} • ${publishedText}` : viewsText;

  // pass the full video object into route state so VideoDetails can play deep-search items
  if (compact) {
    return (
      <Link to={`/${slug}`} state={{ playerVideo: video }} className="group block">
        <div className="flex flex-col mb-2">
          <div className="relative h-24 rounded-xl overflow-hidden bg-slate-800">
            <LazyImage
              src={video?.thumbnails?.[0]?.url || "/default-thumbnail.svg"}
              alt={video?.title || "thumbnails"}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              videoUrl={video?.originalUrl || video?.url}
              videoDuration={video?.lengthSeconds}
            />
            {video?.lengthSeconds && <VideoLength time={video?.lengthSeconds} />}
          </div>

          <div className="flex flex-col mt-2 overflow-hidden text-black dark:text-white">
            <span className="font-semibold line-clamp-2 text-xs leading-snug">
              {video?.title}
            </span>
            <span className="mt-0.5 text-[11px] text-neutral-600 dark:text-neutral-400 truncate">
              {meta}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/${slug}`} state={{ playerVideo: video }} className="group block">
      <div className="flex flex-col mb-8">
        {/* Thumbnail */}
        <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-800 shadow-sm group-hover:shadow-lg transition-all duration-300">
          <LazyImage
            src={video?.thumbnails?.[0]?.url || "/default-thumbnail.svg"}
            alt={video?.title || "thumbnails"}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            videoUrl={video?.originalUrl || video?.url}
            videoDuration={video?.lengthSeconds}
          />
          {video?.lengthSeconds && <VideoLength time={video?.lengthSeconds} />}

          {/* Hover play overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white drop-shadow-lg">
              <MdPlayCircleOutline className="w-12 h-12 md:w-14 md:h-14" />
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="flex flex-col mt-3 overflow-hidden text-black dark:text-white">
          <span className="font-semibold line-clamp-2 text-sm md:text-[15px] leading-snug">
            {video?.title}
          </span>
          <span className="mt-1 text-xs text-neutral-600 dark:text-neutral-400 truncate">
            {meta}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default VideoCard;
